<template>
    <b-modal
        :visible="value"
        size="md"
        centered
        hide-footer
        hide-header
        no-close-on-backdrop
        modal-class="lead-apt-modal"
        content-class="lead-apt-content"
        body-class="p-0"
        @change="$emit('input', $event)"
    >
        <!-- header -->
        <div class="lead-apt-header" :style="{'--apt-accent': currentTypeColor}">
            <div class="lead-apt-header-icon">
                <feather-icon icon="CalendarIcon" size="20"/>
            </div>
            <div>
                <h5 class="mb-0">{{ msg('Book appointment') }}</h5>
                <small v-if="leadName">{{ leadName }}</small>
            </div>
            <b-button variant="flat-light" class="btn-icon ml-auto lead-apt-close" @click="close">
                <feather-icon icon="XIcon" size="16"/>
            </b-button>
        </div>

        <div class="lead-apt-body">
            <validation-observer ref="observer">
                <!-- Terminmodul: physical / phone -->
                <div class="mb-1">
                    <label class="lead-apt-label">{{ msg('Appointment mode') }}</label>
                    <div class="lead-apt-modes">
                        <button v-for="m in modes" :key="m.value" type="button"
                                class="lead-apt-mode" :class="{active: form.mode === m.value}"
                                @click="form.mode = m.value">
                            <feather-icon :icon="m.icon" size="16"/>
                            <span>{{ msg(m.label) }}</span>
                        </button>
                    </div>
                </div>

                <!-- Art des Termins -->
                <div class="mb-1">
                    <label class="lead-apt-label">{{ msg('Appointment type') }}</label>
                    <div class="lead-apt-types">
                        <button v-for="t in types" :key="t.value" type="button"
                                class="lead-apt-type" :class="{active: form.type === t.value}"
                                :style="{'--apt-type-color': t.color}"
                                @click="form.type = t.value">
                            <span class="lead-apt-type-dot"/>
                            <span class="lead-apt-type-name">{{ msg(t.label) }}</span>
                            <b-badge variant="light-secondary">{{ t.duration }} {{ msg('min') }}</b-badge>
                        </button>
                    </div>
                </div>

                <!-- Termin Datum -->
                <div class="mb-1">
                    <label class="lead-apt-label">{{ msg('Appointment date') }} <span class="text-danger">*</span></label>
                    <validation-provider #default="{ errors }" :name="msg('Appointment date')" rules="required">
                        <flat-pickr v-model="form.start" class="form-control"
                                    :config="{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: true, minuteIncrement: 5 }"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                </div>

                <!-- User zuteilen -->
                <div class="mb-1">
                    <label class="lead-apt-label">{{ msg('Assign to user') }} <span class="text-danger">*</span></label>
                    <validation-provider #default="{ errors }" :name="msg('Assign to user')" rules="required">
                        <v-select v-model="form.ownerUserId" :options="userOptions" :reduce="o => o.id"
                                  label="name" append-to-body :clearable="false"
                                  :placeholder="msg('Select user')" :class="{'is-invalid': errors.length}"/>
                        <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                </div>

                <!-- Notes -->
                <div class="mb-1">
                    <label class="lead-apt-label">{{ msg('Notes') }} <small class="text-muted">({{ msg('optional') }})</small></label>
                    <b-form-textarea v-model="form.description" rows="2" max-rows="5"
                                     :placeholder="msg('Agenda, preparation, notes…')"/>
                </div>

                <!-- summary -->
                <div class="lead-apt-summary" :style="{'--apt-accent': currentTypeColor}">
                    <feather-icon :icon="currentModeIcon" size="14" class="mr-50"/>
                    <span>{{ msg(currentTypeLabel) }} · {{ currentTypeDuration }} {{ msg('min') }}</span>
                    <span v-if="form.start" class="ml-auto font-weight-bold">{{ form.start }}</span>
                </div>

                <div class="d-flex justify-content-end mt-1">
                    <b-button variant="outline-secondary" class="mr-1" @click="close">{{ msg('Cancel') }}</b-button>
                    <b-button variant="primary" :disabled="saving" @click="submit">
                        <b-spinner v-if="saving" small class="mr-50"/>
                        <feather-icon v-else icon="CalendarIcon" size="14" class="mr-25"/>
                        {{ msg('Book appointment') }}
                    </b-button>
                </div>
            </validation-observer>
        </div>
    </b-modal>
</template>

<script>
import {ValidationObserver, ValidationProvider} from 'vee-validate'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import {APPOINTMENT_TYPES, appointmentType, appointmentMode} from '@/model/appointment/appointmentEnums'

// Lead flow ("Terminmodul"): physical / phone only
const LEAD_MODES = [
    {value: 'PHYSICAL', label: 'Physical', icon: 'MapPinIcon'},
    {value: 'PHONE', label: 'Phone', icon: 'PhoneIcon'},
];
// Art des Termins: Erstgespräch 30' / Onboarding 60' / Support 10'
const LEAD_TYPES = APPOINTMENT_TYPES.filter((t) =>
    ['FIRST_CONSULTATION', 'ONBOARDING', 'SUPPORT'].includes(t.value));

export default {
    name: 'LeadAppointmentModal',
    components: {ValidationObserver, ValidationProvider, vSelect, flatPickr},
    props: {
        value: {type: Boolean, default: false},
        // {id, name, email, phone} of the lead the appointment is for
        lead: {type: Object, default: null},
    },
    data() {
        return {
            saving: false,
            modes: LEAD_MODES,
            types: LEAD_TYPES,
            form: this.emptyForm(),
        }
    },
    computed: {
        leadName() {
            return this.lead ? this.lead.name : '';
        },
        myId() {
            const p = this.$store.getters['user/myProfile'];
            return p ? p.id : null;
        },
        userOptions() {
            const users = this.$store.getters['appointment/getAssignableUsers'] || [];
            if (this.myId != null && !users.some((u) => u.id === this.myId)) {
                const myName = this.$store.getters['user/myName'];
                return [{id: this.myId, name: myName || ('#' + this.myId)}, ...users];
            }
            return users;
        },
        currentTypeColor() {
            return appointmentType(this.form.type).color;
        },
        currentTypeLabel() {
            return appointmentType(this.form.type).label;
        },
        currentTypeDuration() {
            return appointmentType(this.form.type).duration;
        },
        currentModeIcon() {
            return appointmentMode(this.form.mode).icon;
        },
    },
    watch: {
        value(open) {
            if (open) this.onOpen();
        },
    },
    methods: {
        emptyForm() {
            return {
                mode: 'PHYSICAL',
                type: 'FIRST_CONSULTATION',
                start: '',
                ownerUserId: null,
                description: '',
            };
        },
        onOpen() {
            this.form = this.emptyForm();
            this.form.ownerUserId = this.myId;
            if (!this.userOptions.length) {
                this.$store.dispatch('appointment/loadAssignableUsers');
            }
        },
        close() {
            this.$emit('input', false);
        },
        async submit() {
            const ok = await this.$refs.observer.validate();
            if (!ok) return;
            this.saving = true;
            try {
                const selected = this.$store.getters['data/getSelectedBranchesIds'] || [];
                const typeLabel = this.msg(this.currentTypeLabel);
                const payload = {
                    title: typeLabel + (this.leadName ? ' – ' + this.leadName : ''),
                    description: this.form.description,
                    mode: this.form.mode,
                    type: this.form.type,
                    start: this.form.start,
                    // no end → backend applies the type's default duration
                    ownerUserId: this.form.ownerUserId,
                    contactName: this.leadName || null,
                    contactInfo: this.lead ? (this.lead.email || this.lead.phone || null) : null,
                    phoneNumber: this.form.mode === 'PHONE' && this.lead ? (this.lead.phone || null) : null,
                    leadId: this.lead && this.lead.id != null ? String(this.lead.id) : null,
                    buId: selected.length ? selected[0] : null,
                };
                await this.$store.dispatch('appointment/create', payload);
                this.$store.dispatch('notifications/success', {
                    title: this.msg('Calendar'),
                    message: this.msg('Appointment booked and added to the calendar'),
                }, {root: true});
                this.$emit('saved');
                this.close();
            } catch (e) { /* error notification handled by the store */
            } finally {
                this.saving = false;
            }
        },
    },
}
</script>

<style lang="scss">
/* Unscoped: b-modal content is portaled to <body> (keyed to content-class). */
.lead-apt-content {
    border-radius: .8rem;
    overflow: hidden;
}

.lead-apt-content .lead-apt-header {
    display: flex;
    align-items: center;
    gap: .8rem;
    padding: 1rem 1.25rem;
    background: linear-gradient(120deg, var(--apt-accent, #7367f0), #4b4b9b);
    color: #fff;

    h5 {
        color: #fff;
        font-weight: 700;
    }

    small {
        opacity: .85;
    }
}

.lead-apt-content .lead-apt-header-icon {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    border-radius: .6rem;
    background: rgba(255, 255, 255, .18);
    display: flex;
    align-items: center;
    justify-content: center;
}

.lead-apt-content .lead-apt-close {
    color: #fff !important;
}

.lead-apt-content .lead-apt-body {
    padding: 1.25rem;
}

.lead-apt-content .lead-apt-label {
    font-size: .8rem;
    font-weight: 600;
    margin-bottom: .3rem;
    display: block;
    color: #5e5873;
}

/* mode buttons */
.lead-apt-content .lead-apt-modes {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: .5rem;
}

.lead-apt-content .lead-apt-mode {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .45rem;
    padding: .65rem;
    border-radius: .55rem;
    border: 1px solid #d8d6de;
    background: transparent;
    color: #6e6b7b;
    font-weight: 600;
    transition: all .15s ease;
    cursor: pointer;

    &:hover {
        border-color: #7367f0;
        color: #7367f0;
    }

    &.active {
        background: #7367f0;
        border-color: #7367f0;
        color: #fff;
        box-shadow: 0 4px 12px rgba(115, 103, 240, .35);
    }
}

/* type rows */
.lead-apt-content .lead-apt-types {
    display: flex;
    flex-direction: column;
    gap: .4rem;
}

.lead-apt-content .lead-apt-type {
    display: flex;
    align-items: center;
    gap: .55rem;
    padding: .55rem .75rem;
    border-radius: .55rem;
    border: 1px solid #d8d6de;
    background: transparent;
    color: #5e5873;
    font-weight: 600;
    font-size: .85rem;
    text-align: left;
    transition: all .15s ease;
    cursor: pointer;

    .lead-apt-type-dot {
        width: 10px;
        height: 10px;
        flex: 0 0 10px;
        border-radius: 50%;
        background: var(--apt-type-color);
    }

    .lead-apt-type-name {
        flex: 1;
    }

    &:hover {
        border-color: var(--apt-type-color);
    }

    &.active {
        border-color: var(--apt-type-color);
        box-shadow: inset 0 0 0 1px var(--apt-type-color);
    }
}

.lead-apt-content .lead-apt-summary {
    display: flex;
    align-items: center;
    padding: .6rem .8rem;
    border-radius: .55rem;
    background: rgba(115, 103, 240, .08);
    border-left: 3px solid var(--apt-accent, #7367f0);
    font-size: .82rem;
    color: #5e5873;
}

/* ── dark mode ── */
.dark-layout .lead-apt-content,
.dark-layout .lead-apt-content .modal-body {
    background-color: #283046;
}

.dark-layout .lead-apt-content .lead-apt-label,
.dark-layout .lead-apt-content .lead-apt-type,
.dark-layout .lead-apt-content .lead-apt-summary {
    color: #d0d2d6;
}

.dark-layout .lead-apt-content .lead-apt-mode,
.dark-layout .lead-apt-content .lead-apt-type {
    border-color: #3b4253;
}

.dark-layout .lead-apt-content .lead-apt-mode {
    color: #b4b7bd;

    &.active {
        color: #fff;
    }
}

.dark-layout .lead-apt-content .lead-apt-summary {
    background: rgba(115, 103, 240, .15);
}
</style>
